import React, { useEffect } from 'react';
import { selectProfile } from 'platform/user/selectors';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  fetchInProgressForm,
  removeInProgressForm,
} from 'platform/forms/save-in-progress/actions';
import { subDays } from 'date-fns';
import AskVaLinkWithRouter from '../AskVaLink';
import DefaultStartLink from './DefaultStartLink';
import { askVaFormId } from '../../constants';
import prefillTransformer from '../../config/prefill-transformer';
import QuestionInProgressAlert from './QuestionInProgressAlert';
import { fetchInquiriesIfNeeded } from '../../actions/fetchInquiries';

/** @typedef {'idle' | 'loading' | 'success' | 'error'} FetchInquiriesStatus */

/** @param {{ status: FetchInquiriesStatus, hasHistory: boolean }} params */
function getInboxCardContent({ status, hasHistory }) {
  if (status === 'error') {
    return (
      <div className="vads-u-margin-top--2">
        <va-alert status="error" slim>
          <p className="vads-u-margin-y--0">
            We’re sorry. There’s a problem with our system, and we can’t access
            your Ask VA messages right now. Refresh this page or try again
            later.
          </p>
        </va-alert>
      </div>
    );
  }
  if (status === 'success' && hasHistory) {
    return (
      <>
        <p className="vads-u-margin-y--1">
          You can check the status of all your questions and our answers in one
          place.
        </p>
        <AskVaLinkWithRouter
          href="inbox"
          variant="action.secondary"
          text="Go to your Ask VA inbox"
        />
      </>
    );
  }
  if (status === 'success' && !hasHistory) {
    return (
      <p className="vads-u-margin-top--2 vads-u-margin-bottom--0">
        You haven’t asked any questions yet. After you ask one, you'll be able
        to access your inbox where you can check the status of your questions
        and review our responses.
      </p>
    );
  }
  return (
    <va-loading-indicator
      data-testid="loading-indicator"
      message="Checking your question history..."
    />
  );
}

/** @param {{ formStartLink: string }} props */
export function AuthenticatedIntro({ formStartLink }) {
  const dispatch = useDispatch();
  const { profile, allSavedForms } = useSelector(state => {
    const userProfile = selectProfile(state);
    return {
      profile: userProfile,
      allSavedForms: userProfile?.savedForms,
    };
  });
  const { status, results } = useSelector(
    state => state.askVA.inquiriesHistory,
  );
  const prefillAvailable = profile?.prefillsAvailable?.includes(askVaFormId);
  const savedForm = allSavedForms?.find(form => form.form === askVaFormId);

  useEffect(() => {
    dispatch(fetchInquiriesIfNeeded());
  }, [dispatch]);

  const startWithPrefill = prefillAvailable
    ? () =>
        dispatch(
          fetchInProgressForm(askVaFormId, undefined, true, prefillTransformer),
        )
    : undefined;

  const startNewQuestion = () =>
    dispatch(removeInProgressForm(askVaFormId, undefined, prefillTransformer));
  const continueSavedQuestion = () =>
    dispatch(fetchInProgressForm(askVaFormId, undefined));

  return (
    <div className="intro-auth">
      <div>
        <h2 className="vads-u-margin-top--4">
          Get support or review your questions
        </h2>
        <p className="vads-u-margin-bottom--0">
          You can use Ask VA to ask a question online. You can ask us about
          education benefits, VA disability compensation (pay), healthcare, and
          many other topics.
        </p>
      </div>
      <div className="actions-list">
        {savedForm ? (
          <QuestionInProgressAlert
            onStartNew={startNewQuestion}
            onContinue={continueSavedQuestion}
            expiresAt={
              savedForm.metadata?.expiresAt ||
              Math.floor(subDays(new Date(), 1).getTime() / 1000)
            }
          />
        ) : (
          <DefaultStartLink
            href={formStartLink}
            onStart={startWithPrefill}
            {...{ prefillAvailable }}
          />
        )}
        <va-card>
          <h3 className="vads-u-margin--0">Review your questions</h3>
          {getInboxCardContent({ status, hasHistory: results?.length > 0 })}
        </va-card>
      </div>
    </div>
  );
}

AuthenticatedIntro.propTypes = {
  formStartLink: PropTypes.string.isRequired,
};
